import { App } from "@capacitor/app";
import { useSyncExternalStore } from "react";

// Durable outbox for field actions a driver/seller takes where cell data can
// drop (a farm pickup, a rural drop-off). Each mutation is written to
// IndexedDB BEFORE any network call, so it survives an app kill, then replayed
// when connectivity returns. Modeled on Uber's "optimistic mode": only queue
// actions with a predictable, idempotent response — never payment or anything
// needing live server arbitration (accepting a job two drivers are racing for).
//
// Every entry carries a client-generated id used as an Idempotency-Key header;
// the confirm endpoints treat an already-applied action as success, so a
// replay after a partial send can't double-confirm or double-notify.

const DB_NAME = "stoop-outbox";
const STORE = "mutations";
const FLUSH_INTERVAL_MS = 30_000;

export type OutboxPhoto = {
  // Multipart upload performed at flush time; the returned url is merged into
  // the body under `bodyField` before the confirm POST fires.
  blob: Blob;
  uploadUrl: string;
  formField: string;
  responseUrlKey: string;
  bodyField: string;
};

export type OutboxEntry = {
  id: string;
  createdAt: number;
  kind: string;
  label: string;
  url: string;
  method: string;
  body: Record<string, unknown>;
  photo?: OutboxPhoto;
  attempts: number;
  status: "pending" | "failed";
  lastError?: string;
};

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  // Fallback without Math.random dependence on a single source: mix time + a
  // getRandomValues draw so ids stay unique on platforms lacking randomUUID.
  const buf = new Uint8Array(16);
  c?.getRandomValues?.(buf);
  return `${Date.now().toString(16)}-${Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

async function idbGetAll(): Promise<OutboxEntry[]> {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve((req.result as OutboxEntry[]) ?? []);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(entry: OutboxEntry): Promise<void> {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(id: string): Promise<void> {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

const listeners = new Set<() => void>();
let cachedCount = 0;
let cachedFailed = 0;

async function refreshCounts(): Promise<void> {
  try {
    const all = await idbGetAll();
    cachedCount = all.filter((e) => e.status === "pending").length;
    cachedFailed = all.filter((e) => e.status === "failed").length;
  } catch {
    /* counts stay at last known */
  }
  for (const l of listeners) l();
}

let flushing = false;

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

async function flush(): Promise<void> {
  if (flushing || isOffline()) return;
  flushing = true;
  try {
    const entries = (await idbGetAll())
      .filter((e) => e.status === "pending")
      .sort((a, b) => a.createdAt - b.createdAt);
    for (const entry of entries) {
      try {
        const body: Record<string, unknown> = { ...entry.body };
        if (entry.photo) {
          const form = new FormData();
          form.append(entry.photo.formField, entry.photo.blob, `${entry.id}.jpg`);
          const upRes = await fetch(entry.photo.uploadUrl, { method: "POST", credentials: "include", body: form });
          if (!upRes.ok) {
            if (upRes.status >= 500) throw new Error(`upload ${upRes.status}`);
            entry.status = "failed";
            entry.lastError = `Photo upload rejected (${upRes.status})`;
            await idbPut(entry);
            continue;
          }
          const upJson = (await upRes.json()) as Record<string, string>;
          body[entry.photo.bodyField] = upJson[entry.photo.responseUrlKey];
        }
        const res = await fetch(entry.url, {
          method: entry.method,
          credentials: "include",
          headers: { "Content-Type": "application/json", "Idempotency-Key": entry.id },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          await idbDelete(entry.id);
          if (typeof window !== "undefined") window.dispatchEvent(new Event("stoop-outbox-sync"));
          continue;
        }
        if (res.status >= 500) throw new Error(`server ${res.status}`);
        // 409/425 are transient preconditions (e.g. the seller hasn't marked
        // the order ready yet) that resolve on their own — leave the entry
        // pending and let the next flush retry it.
        if (res.status === 409 || res.status === 425) {
          entry.attempts += 1;
          await idbPut(entry);
          continue;
        }
        // Any other 4xx is a permanent rejection (validation, a distance
        // mismatch the driver never re-confirmed). Park it as failed for the
        // UI to surface rather than retry it forever.
        const text = await res.text().catch(() => "");
        entry.status = "failed";
        entry.lastError = text.slice(0, 200) || `HTTP ${res.status}`;
        await idbPut(entry);
      } catch (err) {
        // Network-level failure: still offline or a transient blip. Leave the
        // entry pending and stop — the next trigger retries from the top.
        entry.attempts += 1;
        entry.lastError = String((err as { message?: string })?.message || err);
        await idbPut(entry);
        break;
      }
    }
  } finally {
    flushing = false;
    await refreshCounts();
  }
}

export async function enqueue(
  input: Omit<OutboxEntry, "id" | "createdAt" | "attempts" | "status"> & { id?: string },
): Promise<void> {
  const entry: OutboxEntry = {
    id: input.id ?? newId(),
    createdAt: Date.now(),
    attempts: 0,
    status: "pending",
    kind: input.kind,
    label: input.label,
    url: input.url,
    method: input.method,
    body: input.body,
    photo: input.photo,
  };
  await idbPut(entry);
  await refreshCounts();
  void flush();
}

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg = String((err as { message?: string })?.message || "").toLowerCase();
  return /failed to fetch|load failed|network|timed out|timeout/.test(msg);
}

export class HttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message || `HTTP ${status}`);
    this.status = status;
  }
}

// Online-first-else-queue for a field action. Tries the request inline (so
// success/validation errors surface immediately); on a dropped connection it
// persists the same entry — same id, so the Idempotency-Key matches — to the
// outbox for replay. HTTP errors (4xx/5xx) throw an HttpError for the caller to
// handle. Returns "queued" when it went to the outbox, "sent" when it landed.
export async function sendOrQueue(
  input: Omit<OutboxEntry, "id" | "createdAt" | "attempts" | "status"> & { id?: string },
): Promise<"sent" | "queued"> {
  const entry = { ...input, id: input.id ?? newId() };
  if (isOffline()) {
    await enqueue(entry);
    return "queued";
  }
  try {
    const body: Record<string, unknown> = { ...entry.body };
    if (entry.photo) {
      const form = new FormData();
      form.append(entry.photo.formField, entry.photo.blob, `${entry.id}.jpg`);
      const upRes = await fetch(entry.photo.uploadUrl, { method: "POST", credentials: "include", body: form });
      if (!upRes.ok) throw new HttpError(upRes.status, await upRes.text().catch(() => ""));
      const upJson = (await upRes.json()) as Record<string, string>;
      body[entry.photo.bodyField] = upJson[entry.photo.responseUrlKey];
    }
    const res = await fetch(entry.url, {
      method: entry.method,
      credentials: "include",
      headers: { "Content-Type": "application/json", "Idempotency-Key": entry.id },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new HttpError(res.status, await res.text().catch(() => ""));
    return "sent";
  } catch (err) {
    if (isNetworkError(err)) {
      await enqueue(entry);
      return "queued";
    }
    throw err;
  }
}

export async function retryFailed(): Promise<void> {
  const all = await idbGetAll();
  for (const e of all) {
    if (e.status === "failed") {
      e.status = "pending";
      e.lastError = undefined;
      await idbPut(e);
    }
  }
  await refreshCounts();
  void flush();
}

export async function discard(id: string): Promise<void> {
  await idbDelete(id);
  await refreshCounts();
}

export function listPending(): Promise<OutboxEntry[]> {
  return idbGetAll();
}

let started = false;
export function initOutbox(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", () => void flush());
  App.addListener("resume", () => void flush()).catch(() => {});
  window.setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  void refreshCounts();
  void flush();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useOutbox(): { pending: number; failed: number } {
  const pending = useSyncExternalStore(
    subscribe,
    () => cachedCount,
    () => 0,
  );
  const failed = useSyncExternalStore(
    subscribe,
    () => cachedFailed,
    () => 0,
  );
  return { pending, failed };
}
